'use client';

import * as React from 'react';
import { useEffect, useState } from 'react';
import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';

import { CurrencyCatalogProvider } from '@/lib/currency/catalog';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { broadcastQueryClient } from '@tanstack/query-broadcast-client-experimental';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { ApiAbortedError, ApiAuthError, isAuthError } from '@/lib/api/errors';
import { publishQueryFailure, subjectFromQueryKey } from '@/lib/query/failure-bus';
import { ensureMockReady } from '@/lib/mock/start';
import { startMockWsPublisher, stopMockWsPublisher } from '@/lib/mock/ws';
import { useSessionStore } from '@/lib/auth/session';
import { wsManager } from '@/lib/ws/manager';
import { getApiMode } from '@/lib/api';
import type { ISODateTime } from '@/types/money';


/**
 * Application-wide TanStack Query provider.
 *
 *   • Boots MSW (if mode != 'real') before any query fires, so the first
 *     `useQuery` lands on a registered worker rather than the real network.
 *   • Seeds a dev token on first mount so the mock /healthz returns 200
 *     instead of 401. P0-D replaces this with the paste-on-first-load flow.
 *   • `staleTime: 30s` — operator-grade default; tiles that need finer
 *     freshness override per-query.
 *   • `retry: 0` on queries AND mutations — the api client already retries
 *     network/timeout/5xx with exponential backoff; React Query retrying
 *     on top would compound.
 *   • `refetchOnWindowFocus: false` — refetching when the trader switches
 *     desktops would create surprise jitter. WS push handles live data;
 *     polling resumes on interval.
 *   • Persisted to localStorage so back-button navigation rehydrates
 *     instantly. Keys are versioned (`te:rq-cache:v1`) so a schema bump
 *     invalidates the cache cleanly.
 *   • `broadcastQueryClient` syncs cache keys across tabs so an audit
 *     event in one tab refreshes the audit log in another.
 *   • Global `onError` for queries fans 401s to `signOut('401')`. Per-tile
 *     ErrorBoundaries handle the rest (added in P0-D).
 */
export function Providers({ children }: { children: React.ReactNode }) {
  // QueryClient must NOT be recreated across renders.
  const [client] = useState(() => {
    const qc = new QueryClient({
      queryCache: new QueryCache({
        onError: (err, query) => {
          if (isAuthError(err)) {
            // ApiAuthError fires signOut inside the api client already.
            return;
          }
          // An aborted request is normal (navigation / unmount cancels it),
          // not a failure worth telling the operator about.
          if (err instanceof ApiAbortedError) {
            return;
          }
          // Everything else previously stopped here, which is why a failed
          // query renders as an empty list rather than an error. Publish it
          // so OperatorEventToasts can surface it. See lib/query/failure-bus.
          publishQueryFailure({
            key: JSON.stringify(query.queryKey),
            subject: `${subjectFromQueryKey(query.queryKey)} unavailable`,
            detail: err instanceof Error ? err.message : String(err),
          });
        },
      }),
      mutationCache: new MutationCache({
        onError: (err) => {
          if (err instanceof ApiAuthError) {
            return;
          }
        },
      }),
      defaultOptions: {
        queries: {
          staleTime: 30_000,
          gcTime: 5 * 60_000,
          // The api client's request() already retries network/timeout/5xx
          // with exponential backoff; React Query retrying on top would
          // compound. Keep this at 0.
          retry: 0,
          refetchOnWindowFocus: false,
          refetchOnReconnect: true,
        },
        mutations: {
          retry: 0,
        },
      },
    });

    if (typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined') {
      try {
        broadcastQueryClient({ queryClient: qc, broadcastChannel: 'te:rq-cache' });
      } catch {
        /* old browsers without BroadcastChannel — graceful degrade */
      }
    }
    return qc;
  });

  const [persister] = useState(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      return createSyncStoragePersister({
        storage: window.localStorage,
        key: 'te:rq-cache:v2',
        throttleTime: 1_000,
      });
    } catch {
      return null;
    }
  });

  useEffect(() => {
    let cancelled = false;
    // Boot MSW. Queries that fired before this lands will fail their first
    // attempt, then retry (see defaultOptions.queries.retry) and hit the
    // worker. We also invalidate explicitly to recover error-state queries
    // that won't refire on their own.
    void ensureMockReady().then(() => {
      if (cancelled) {
        return;
      }
      void client.invalidateQueries();
    });

    // Seed a dev token ONLY in pure mock mode AND a development build.
    // In hybrid / real mode the operator must sign in with a real admin
    // api_key (validated against `/admin/operators/me`) so the dashboard
    // hits the live backend with a credential that backend will actually
    // accept. getApiMode() itself fails closed to 'real' when the env var
    // is unset, so a dropped env file can never auto-impersonate.
    const apiMode = getApiMode();
    if (
      apiMode === 'mock' &&
      process.env.NODE_ENV === 'development' &&
      useSessionStore.getState().accessToken === null
    ) {
      useSessionStore.getState().__devImpersonate(
        {
          id: 'op-001',
          email: 'dev@invasset.example',
          display_name: 'Dev Operator',
          role: 'admin',
          status: 'active',
          four_eyes_eligible: true,
          desk: 'Engineering',
          created_at: new Date().toISOString() as ISODateTime,
        },
        'mock-bearer-token',
      );
    }

    // Configure + start the WS manager.
    //
    // Real mode: the stream endpoint is per-operator
    // (/ws/streams/user/{operator_id}), so the URL must come from the
    // SIGNED-IN operator — never a hardcoded id. The session store may not
    // be hydrated yet on first mount (persist rehydration / sign-in happen
    // later), so we sync on every session change: idle until an operator id
    // exists, reconnect if the id or token changes, drop to idle on
    // sign-out.
    const mode = getApiMode();
    const wsBase = process.env.NEXT_PUBLIC_WS_BASE_URL ?? '';
    let unsubscribeSession: (() => void) | null = null;

    if (mode === 'real') {
      let lastUrl: string | null = null;
      let lastToken: string | null = null;
      let started = false;

      const syncWs = () => {
        const { me, accessToken } = useSessionStore.getState();
        const url =
          wsBase !== '' && me !== null
            ? `${wsBase}/ws/streams/user/${encodeURIComponent(me.id)}`
            : null;
        if (started && url === lastUrl && accessToken === lastToken) {
          return;
        }
        lastUrl = url;
        lastToken = accessToken;
        if (started) {
          wsManager.stop();
        }
        wsManager.configure({ url, bearerToken: accessToken });
        wsManager.start();
        started = true;
      };

      syncWs();
      unsubscribeSession = useSessionStore.subscribe(syncWs);
    } else {
      wsManager.configure({
        url: null,
        bearerToken: useSessionStore.getState().accessToken,
      });
      wsManager.start();
      startMockWsPublisher();
    }

    return () => {
      cancelled = true;
      unsubscribeSession?.();
      stopMockWsPublisher();
      wsManager.stop();
    };
  }, [client]);

  if (persister === null) {
    return (
      <PersistQueryClientProvider
        client={client}
        persistOptions={{
          persister: createSyncStoragePersister({
            storage: {
              getItem: () => null,
              setItem: () => undefined,
              removeItem: () => undefined,
            },
            key: 'te:rq-cache:v2',
          }),
        }}
      >
        <CurrencyCatalogProvider>{children}</CurrencyCatalogProvider>
        {process.env.NODE_ENV === 'development' ? (
          <ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} />
        ) : null}
      </PersistQueryClientProvider>
    );
  }

  return (
    <PersistQueryClientProvider
      client={client}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24,
        buster: 'v2',
      }}
    >
      <CurrencyCatalogProvider>{children}</CurrencyCatalogProvider>
      {process.env.NODE_ENV === 'development' ? (
        <ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} />
      ) : null}
    </PersistQueryClientProvider>
  );
}
